// TipButton — small "Watch how" button that opens a compact narrated popover.
// Fetches the tips manifest once (module-level cache), plays the tip's audio
// via the same /api/onboarding/audio/{id}.mp3 endpoint as the User Guide.
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { HelpCircle, X, Play, Pause, Loader2, Sparkles } from 'lucide-react';
import { API } from '../utils/api';

// Module-level cache so we only hit the manifest once per session.
let _tipsCache = null;
let _tipsPromise = null;
const fetchTips = () => {
  if (_tipsCache) return Promise.resolve(_tipsCache);
  if (_tipsPromise) return _tipsPromise;
  _tipsPromise = axios.get(`${API}/onboarding/slides`).then(r => {
    _tipsCache = (r.data.tips || []).reduce((acc, t) => { acc[t.id] = t; return acc; }, {});
    return _tipsCache;
  }).catch(() => ({}));
  return _tipsPromise;
};

const TipButton = ({ tipId, label = 'Watch how' }) => {
  const [tip, setTip] = useState(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const btnRef = useRef(null);
  const [popStyle, setPopStyle] = useState({ top: 0, left: 0 });

  useEffect(() => {
    fetchTips().then(map => setTip(map[tipId] || null));
  }, [tipId]);

  // Position the popover under the button on open.
  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const popWidth = 320;
      let left = r.left + window.scrollX;
      const overflow = left + popWidth - (window.scrollX + window.innerWidth - 12);
      if (overflow > 0) left -= overflow;
      setPopStyle({ top: r.bottom + window.scrollY + 6, left });
    }
  }, [open]);

  // Cleanup audio on close/unmount
  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [open]);

  const handlePlay = async () => {
    if (!tip) return;
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setLoading(true);
    try {
      // Warm the cache before playing (idempotent, cheap)
      await axios.post(`${API}/onboarding/generate`).catch(() => {});
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        setPlaying(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tip) {
    return (
      <button ref={btnRef} disabled style={btnStyle(true)} data-testid={`tip-btn-${tipId}`}>
        <HelpCircle className="w-3 h-3" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        style={btnStyle(false)}
        data-testid={`tip-btn-${tipId}`}
        title={`Play a 15-second explainer: ${tip.title}`}
      >
        <Sparkles className="w-3 h-3" style={{ color: 'var(--gold)' }} />
        <span>{label}</span>
      </button>

      {open && ReactDOM.createPortal(
        <>
          {/* Backdrop to close on outside-click */}
          <div onClick={() => setOpen(false)}
               style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
          <div
            data-testid={`tip-popover-${tipId}`}
            style={{
              position: 'absolute', ...popStyle, width: 320, zIndex: 9999,
              background: '#fff', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              border: '1px solid #e5e7eb', overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 14px', background: tip.accent, color: '#fff', position: 'relative' }}>
              <div style={{ fontSize: 11, opacity: 0.8, letterSpacing: '.5px' }}>15-SECOND EXPLAINER</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{tip.title}</div>
              <button onClick={() => setOpen(false)}
                      style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}
                      aria-label="Close tip">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.6, marginBottom: 12 }}>
                {tip.narration}
              </div>
              <button
                onClick={handlePlay}
                disabled={loading}
                data-testid={`tip-play-${tipId}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: tip.accent, color: '#fff', border: 'none', padding: '7px 14px',
                  borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : (playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />)}
                {playing ? 'Pause' : 'Play with voice'}
              </button>
              <audio
                ref={audioRef}
                src={`${API}/onboarding/audio/${tip.id}.mp3`}
                preload="none"
                onEnded={() => setPlaying(false)}
                onPause={() => setPlaying(false)}
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

const btnStyle = (disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: '#fff', border: '1px solid #e5e7eb', color: '#4b5563',
  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  transition: '.15s',
});

export default TipButton;
